import React, { useRef, useState } from "react";
import moment from "moment";
import Papa from "papaparse";
import UIService from "../../../services/UIService";
import IQBOReprocessOrderRepAssignmentsDTO, {
    IQBOReprocessOrderRepAssignmentsResult,
    QBORepUserTarget,
} from "../../../models/DTO/IQBOReprocessOrderRepAssignmentsDTO";
import IUserUI from "../../../models/IUserUI";
import ISupplierModel from "../../../models/ISupplierModel";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import INotification from "../../../models/INotification";
import NotificationLevelEnum from "../../../models/NotificationLevelEnum";
import NotificationPositionEnum from "../../../models/NotificationPositionEnum";
import QboService from "../../../services/QboService";
import OwButton from "../../shared/OwButton/OwButton";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import OwToggle from "../../shared/OwToggle/OwToggle";
import WhatsThis from "../../shared/WhatsThis/WhatsThis";

// Titles are translation keys; render with translateTitle on OwSelectOption.
export const qboRepAssignmentOptions = [
    { title: "order_owner", value: "user_id" },
    { title: "commission_user", value: "commission_user_id" },
];

const totalLabels = {
    totalProcessed: "Processed",
    totalUpdated: "Updated",
    totalUnchanged: "Unchanged",
    totalUnresolved: "Unresolved",
    totalErrors: "Errors",
    totalRepUsersAssigned: "Order owners assigned",
    totalCommissionUsersAssigned: "Commission users assigned",
    totalRepGroupsAssigned: "Rep groups assigned",
};
const totalKeys = Object.keys(totalLabels) as (keyof typeof totalLabels)[];
const emptyResult = (): IQBOReprocessOrderRepAssignmentsResult => ({
    totalProcessed: 0,
    totalUpdated: 0,
    totalUnchanged: 0,
    totalUnresolved: 0,
    totalErrors: 0,
    totalRepUsersAssigned: 0,
    totalCommissionUsersAssigned: 0,
    totalRepGroupsAssigned: 0,
    unresolvedDetails: [],
    errorDetails: [],
    nextAfterId: null,
});

type Props = {
    startDate?: string;
    endDate?: string;
    docNumber?: string;
    onLockedChange?: (running: boolean) => void;
    userModel: IUserUI;
    supplierModel: ISupplierModel;
    notifyFn: (data: IErrorDTO | null, notification?: INotification, isFormatted?: boolean) => void;
};

export default function QboReprocessOrderAssignments({
    userModel,
    supplierModel,
    notifyFn,
    onLockedChange,
    startDate = "",
    endDate = "",
    docNumber = "",
}: Props) {
    const [assignmentTarget, setAssignmentTarget] = useState<QBORepUserTarget | "">(
        supplierModel.Supplier_Integration?.qbo_pull_rep_user_target || "user_id"
    );
    const [repGroupOnly, setRepGroupOnly] = useState(false);
    const [running, setRunning] = useState(false);
    const [requestError, setRequestError] = useState("");
    const [validationError, setValidationError] = useState("");
    const [result, setResult] = useState<IQBOReprocessOrderRepAssignmentsResult | null>(null);
    // Synchronous guard also prevents clicks before React commits the disabled state.
    const active = useRef(false);
    const pending = useRef<IQBOReprocessOrderRepAssignmentsDTO | null>(null);
    const totals = useRef(emptyResult());

    const process = async (retry = false) => {
        if (active.current) return;
        if (!retry) {
            if (
                [startDate, endDate].some(
                    (date) => date && (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !moment(date, "YYYY-MM-DD", true).isValid())
                )
            ) {
                setValidationError("Dates must be valid dates in YYYY-MM-DD format.");
                return;
            }
            if (startDate && endDate && startDate > endDate) {
                setValidationError("Invoice start date must be no later than end date.");
                return;
            }
            pending.current = {
                ...(repGroupOnly ? { repGroupOnly: true } : { assignmentTarget: assignmentTarget as QBORepUserTarget }),
                limit: 200,
                afterId: 0,
                ...(startDate ? { startDate } : {}),
                ...(endDate ? { endDate } : {}),
                ...(docNumber.trim() ? { docNumber: docNumber.trim() } : {}),
            };
            totals.current = emptyResult();
            setResult(totals.current);
        }
        if (!pending.current) return;
        active.current = true;
        setRunning(true);
        onLockedChange?.(true);
        setValidationError("");
        setRequestError("");
        try {
            const seenCursors = new Set<number>();
            while (pending.current) {
                const cursor = pending.current.afterId ?? 0;
                if (seenCursors.has(cursor)) {
                    throw new Error("Reprocessing stopped: the server returned a cursor that was already processed.");
                }
                seenCursors.add(cursor);
                const batch = await QboService.reprocessOrderRepAssignments(pending.current, userModel, supplierModel);
                const next = { ...totals.current, nextAfterId: batch.nextAfterId };
                totalKeys.forEach((key) => {
                    next[key] += batch[key];
                });
                next.unresolvedDetails = [...next.unresolvedDetails, ...batch.unresolvedDetails];
                next.errorDetails = [...next.errorDetails, ...batch.errorDetails];
                totals.current = next;
                setResult(next);
                pending.current = batch.nextAfterId === null ? null : { ...pending.current, afterId: batch.nextAfterId };
            }
            const hasIssues =
                totals.current.totalErrors > 0 || totals.current.totalUnresolved > 0 || totals.current.errorDetails.length > 0;
            notifyFn(null, {
                level: hasIssues ? NotificationLevelEnum.warning : NotificationLevelEnum.success,
                position: NotificationPositionEnum.tr,
                message: hasIssues
                    ? "Reprocessing finished with errors or unresolved mappings. Review the results."
                    : "Order assignment reprocessing completed.",
                skipMessageTranslate: true,
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : "Could not reprocess order assignments.";
            setRequestError(message);
            notifyFn(null, {
                level: NotificationLevelEnum.error,
                position: NotificationPositionEnum.tr,
                message,
                skipMessageTranslate: true,
            });
        } finally {
            active.current = false;
            setRunning(false);
            onLockedChange?.(!!pending.current);
        }
    };

    const locked = running || !!requestError;
    return (
        <section className="qbo-config mt-3 p-3 border rounded" aria-label="Reprocess imported order assignments">
            <h5>Reprocess imported order assignments</h5>
            <WhatsThis subject="help_text_qbo_reprocess_order_assignments" />
            <OwToggle
                name="repGroupOnly"
                label="Rep group only"
                disableTranslation
                active={repGroupOnly}
                disabled={locked}
                wrapperClassName="mb-3"
                fn={(e) => {
                    const enabled = !!e.target.value;
                    setRepGroupOnly(enabled);
                    setAssignmentTarget(enabled ? "" : supplierModel.Supplier_Integration?.qbo_pull_rep_user_target || "user_id");
                }}
            />
            <div className="row g-3">
                <div className="col-12 col-md-6">
                    <label htmlFor="reprocessAssignmentTarget">Assign rep to</label>
                    <OwSelectOption
                        id="reprocessAssignmentTarget"
                        name="assignmentTarget"
                        options={qboRepAssignmentOptions}
                        titleProp="title"
                        valueProp="value"
                        translateTitle={true}
                        showBlankFirst={repGroupOnly}
                        selectedOption={{ assignmentTarget }}
                        selectedOptionProp="assignmentTarget"
                        isDisabled={locked || repGroupOnly}
                        fn={(e: React.ChangeEvent<HTMLSelectElement>) => setAssignmentTarget(e.target.value as QBORepUserTarget)}
                    />
                </div>
            </div>
            {validationError && (
                <div role="alert" className="alert alert-danger mt-2">
                    {validationError}
                </div>
            )}
            {requestError && (
                <div role="alert" className="alert alert-danger mt-2">
                    {requestError} Completed progress is preserved. Retry the failed batch with the same filters.
                </div>
            )}
            <div className="d-flex gap-2 mt-3">
                <OwButton
                    title="Reprocess assignments"
                    ariaLabel="Reprocess assignments"
                    skipTranslate
                    fn={() => void process()}
                    disabled={locked}
                    active={running}
                />
                {requestError && (
                    <>
                        <OwButton title="Retry failed batch" skipTranslate fn={() => void process(true)} disabled={running} />
                        <OwButton
                            title="Reset run"
                            skipTranslate
                            className="btn-secondary"
                            disabled={running}
                            fn={() => {
                                pending.current = null;
                                onLockedChange?.(false);
                                setRequestError("");
                                setResult(null);
                            }}
                        />
                    </>
                )}
            </div>
            {result && (
                <div className="mt-3" aria-live="polite">
                    <p>
                        {running
                            ? "Processing…"
                            : requestError
                              ? "Paused: request failed."
                              : result.totalErrors || result.errorDetails.length
                                ? "Finished with errors."
                                : result.totalUnresolved
                                  ? "Finished with unresolved mappings."
                                  : "Reprocessing completed."}
                    </p>
                    <dl className="row">
                        {totalKeys.map((key) => (
                            <React.Fragment key={key}>
                                <dt className="col-8">{totalLabels[key]}</dt>
                                <dd className="col-4" data-testid={key}>
                                    {result[key]}
                                </dd>
                            </React.Fragment>
                        ))}
                    </dl>
                    <p className="text-muted">
                        Unresolved counts can overlap updated or unchanged counts: an order can receive a rep group without a linked user.
                    </p>
                    {result.unresolvedDetails.length > 0 && (
                        <>
                            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                                <h6 className="mb-0">Unresolved mappings</h6>
                                <OwButton
                                    title="Download unresolved CSV"
                                    skipTranslate
                                    leftIconClass="fas fa-download"
                                    fn={() =>
                                        UIService.downloadCSVData(
                                            Papa.unparse(result.unresolvedDetails, {
                                                columns: ["orderGuid", "reason"],
                                                escapeFormulae: true,
                                            }),
                                            "qbo-unresolved-order-assignments.csv"
                                        )
                                    }
                                />
                            </div>
                            <ul>
                                {result.unresolvedDetails.map((detail, index) => (
                                    <li key={index}>
                                        {detail.orderGuid}: {detail.reason}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {result.errorDetails.length > 0 && (
                        <>
                            <h6>Individual errors</h6>
                            <ul>
                                {result.errorDetails.map((detail, index) => (
                                    <li key={index}>
                                        {detail.orderGuid}: {detail.error}
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            )}
        </section>
    );
}
